import {
  FC,
  ReactNode,
  InputHTMLAttributes,
  SyntheticEvent,
  ChangeEventHandler,
  useCallback,
} from 'react';
import cn from 'classnames';
import { ErrorMessage } from '../../common/ErrorMessage';

export interface IRadioField {
  label: string | ReactNode;
  value: string | number;
}

export interface IRadioFieldProps
  extends Omit<Partial<InputHTMLAttributes<HTMLInputElement>>, 'onChange' | 'type'> {
  id?: string;
  name: string;
  onChange?: (name: string, value: string | number, e?: SyntheticEvent<any>) => void;
  options: IRadioField[];
  label?: ReactNode | string;
  error?: string;
  classNames?: {
    root?: string;
    label?: string;
    error?: string;
    options?: string;
    option?: string;
  };
  disabled?: boolean;
  required?: boolean;
}

export const RadioField: FC<IRadioFieldProps> = (props) => {
  const { name, label, options, classNames, error, disabled, required, onChange, value, ...rest } =
    props;

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    onChange && onChange(name, e.target.value, e);
  }, []);

  return (
    <div
      className={cn(
        'input-radio',
        { 'input-radio__invalid': error },
        { 'input-radio__disabled': disabled },
        { 'input-radio__required': required },
        classNames?.root
      )}
    >
      {label && <div className={cn('input-radio__label', classNames?.label)}>{label}</div>}
      <div className={cn('input-radio__options', classNames?.options)}>
        {options.map((option) => (
          <div key={option.value} className={cn('input-radio__option', classNames?.option)}>
            <input
              name={name}
              type="radio"
              id={`${name}-${option.value}`}
              value={option.value}
              checked={option.value === value}
              onChange={handleChange}
              disabled={disabled}
              {...rest}
            />
            <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
          </div>
        ))}
      </div>
      {error && <ErrorMessage classError={classNames?.error} message={error} />}
    </div>
  );
};
